import { useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { useApi } from "@/lib/useApi";
import { api, apiErr, API } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, EmptyState } from "@/components/common";
import { BookOpen, Plus, Trash2, Pencil, FileText, Loader2, X, Paperclip, ExternalLink, Upload, Grid3x3, Sparkles } from "lucide-react";
import { fDateTime } from "@/lib/format";
import { toast } from "sonner";

const CAT_LABELS = { policy: "Policy", rutin: "Rutin", arbetsbeskrivning: "Arbetsbeskrivning", rollbeskrivning: "Rollbeskrivning", kompetensmatris: "Kompetensmatris", internutbildning: "Internutbildning", ovrigt: "Övrigt" };
const CAT_ORDER = ["policy", "rutin", "arbetsbeskrivning", "rollbeskrivning", "kompetensmatris", "internutbildning", "ovrigt"];
const CATS = CAT_ORDER.map((v) => ({ v, label: CAT_LABELS[v] }));
const catLabel = (c) => CAT_LABELS[c] || "Övrigt";
const emptyForm = { id: null, title: "", category: "policy", body: "", file_path: null, file_name: null, tags: [] };

function TagEditor({ tags, setTags }) {
  const [input, setInput] = useState("");
  const add = () => {
    const v = input.trim().toLowerCase();
    if (v && !tags.includes(v)) setTags([...tags, v]);
    setInput("");
  };
  return (
    <div data-testid="lib-tag-editor">
      <div className="flex flex-wrap gap-1.5">
        {tags.map((tg) => (
          <span key={tg} className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs" data-testid={`lib-tag-chip-${tg}`}>
            {tg}
            <button type="button" onClick={() => setTags(tags.filter((x) => x !== tg))} data-testid={`lib-tag-rm-${tg}`}><X className="h-3 w-3" /></button>
          </span>
        ))}
      </div>
      <Input value={input} onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } }}
        onBlur={add} placeholder="Skriv nyckelord och tryck Enter" className="mt-2" data-testid="lib-tag-input" />
    </div>
  );
}

function DocForm({ initial, onDone, onCancel }) {
  const { t } = useLang();
  const [f, setF] = useState(initial || emptyForm);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads/document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((s) => ({ ...s, file_path: r.data.path, file_name: r.data.name }));
    } catch (err) { toast.error(apiErr(err)); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const save = async () => {
    if (!f.title.trim()) return toast.error(t("enter_title"));
    setBusy(true);
    try {
      const payload = { title: f.title, category: f.category, body: f.body || null, file_path: f.file_path, file_name: f.file_name, tags: f.tags || [] };
      if (f.id) { await api.patch(`/library/${f.id}`, payload); toast.success(t("lib_updated")); }
      else { await api.post("/library", payload); toast.success(t("lib_created")); }
      onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Card className="mb-6 grid gap-3 p-4" data-testid="lib-form">
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="sm:col-span-2"><Label>{t("title_lbl")}</Label><Input value={f.title} onChange={(e) => setF((s) => ({ ...s, title: e.target.value }))} data-testid="lib-title" /></div>
        <div><Label>{t("lib_cat")}</Label>
          <Select value={f.category} onValueChange={(v) => setF((s) => ({ ...s, category: v }))}>
            <SelectTrigger data-testid="lib-cat"><SelectValue /></SelectTrigger>
            <SelectContent>{CATS.map((c) => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}</SelectContent>
          </Select></div>
      </div>
      <div><Label>{t("lib_body")}</Label><Textarea rows={4} value={f.body || ""} onChange={(e) => setF((s) => ({ ...s, body: e.target.value }))} data-testid="lib-body" /></div>
      <div><Label>Sökbara nyckelord</Label><TagEditor tags={f.tags || []} setTags={(tags) => setF((s) => ({ ...s, tags }))} /></div>
      <div className="flex flex-wrap items-center gap-2">
        <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border px-3 py-2 text-sm hover:bg-secondary" data-testid="lib-file-btn">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />} {t("lib_file_add")}
          <input ref={fileRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={onFile} data-testid="lib-file-input" />
        </label>
        {f.file_path && (
          <span className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-xs">
            <FileText className="h-3.5 w-3.5" />{f.file_name || "fil"}
            <button onClick={() => setF((s) => ({ ...s, file_path: null, file_name: null }))} data-testid="lib-file-remove"><X className="h-3.5 w-3.5" /></button>
          </span>
        )}
        <div className="ml-auto flex gap-2">
          {onCancel && <Button variant="ghost" onClick={onCancel} data-testid="lib-cancel">{t("cancel")}</Button>}
          <Button onClick={save} disabled={busy || uploading} data-testid="lib-save"><Plus className="mr-1 h-4 w-4" />{t("lib_save")}</Button>
        </div>
      </div>
    </Card>
  );
}

function DocCard({ d, canManage, onEdit, onChanged }) {
  const { t } = useLang();
  const del = async () => { if (!window.confirm(t("lib_confirm_delete"))) return; try { await api.delete(`/library/${d.id}`); onChanged(); toast.success(t("removed") || "OK"); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <Card className="p-4" data-testid={`lib-doc-${d.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <FileText className="h-4 w-4 text-accent" />
        <span className="font-medium">{d.title}</span>
        <Badge variant="outline">{catLabel(d.category)}</Badge>
        {d.industry && <Badge variant="secondary" data-testid={`lib-ind-badge-${d.id}`}>{d.industry}</Badge>}
        <span className="ml-auto text-xs text-muted-foreground">{t("lib_by", { name: d.created_by_name })} · {fDateTime(d.updated_at || d.created_at)}</span>
      </div>
      {d.summary ? <p className="mt-2 text-sm italic text-muted-foreground" data-testid={`lib-summary-${d.id}`}>{d.summary}</p>
        : d.body && <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">{d.body}</p>}
      {(d.tags || []).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`lib-tags-${d.id}`}>
          {d.tags.map((tg) => <span key={tg} className="rounded-full bg-secondary px-2 py-0.5 text-[11px] text-muted-foreground" data-testid={`lib-tag-${d.id}-${tg}`}>#{tg}</span>)}
        </div>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {d.file_path && (
          <a href={`${API}/files/${d.file_path}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-accent underline" data-testid={`lib-open-${d.id}`}>
            <ExternalLink className="h-4 w-4" />{t("lib_open_file")}
          </a>
        )}
        {d.category === "kompetensmatris" && (
          <Link to="/kompetensmatris" className="inline-flex items-center gap-1 text-sm text-accent underline" data-testid={`lib-matrix-${d.id}`}>
            <Grid3x3 className="h-4 w-4" />Öppna i kompetensmatris
          </Link>
        )}
        {canManage && (
          <div className="ml-auto flex gap-1">
            <Button size="sm" variant="ghost" onClick={() => onEdit(d)} data-testid={`lib-edit-${d.id}`}><Pencil className="h-4 w-4" /></Button>
            <Button size="sm" variant="ghost" onClick={del} data-testid={`lib-del-${d.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
          </div>
        )}
      </div>
    </Card>
  );
}

export default function Bibliotek() {
  const { t } = useLang();
  const { data, reload } = useApi("/library");
  const [q, setQ] = useState("");
  const [industry, setIndustry] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const canManage = !!data?.can_manage;
  const feedPosts = data?.feed_posts || [];
  const { data: cvData } = useApi("/cv");
  const upRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [retagging, setRetagging] = useState(false);
  const retag = async () => {
    setRetagging(true);
    try {
      const r = await api.post("/library/retag");
      toast.success(r.data.updated ? `Nyckelord genererade för ${r.data.updated} dokument` : "Alla dokument har redan nyckelord");
      reload();
    } catch (e) { toast.error(apiErr(e)); } finally { setRetagging(false); }
  };
  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const counts = {};
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/library/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        counts[r.data.category] = (counts[r.data.category] || 0) + 1;
      } catch (err) { toast.error(`${file.name}: ${apiErr(err)}`); }
    }
    setUploading(false);
    if (upRef.current) upRef.current.value = "";
    const summary = Object.entries(counts).map(([c, n]) => `${n} ${catLabel(c)}`).join(", ");
    if (summary) toast.success(`Uppladdat & sorterat: ${summary}`);
    reload();
  };

  const filtered = useMemo(() => {
    let documents = data?.documents || [];
    if (industry) documents = documents.filter((d) => d.category === "kompetensmatris" && (d.industry || "").toLowerCase() === industry);
    const needle = q.trim().toLowerCase();
    if (needle) documents = documents.filter((d) => `${d.title} ${d.body || ""} ${d.summary || ""} ${(d.tags || []).join(" ")}`.toLowerCase().includes(needle));
    return documents;
  }, [data, q, industry]);

  const groups = useMemo(() => {
    const g = {};
    filtered.forEach((d) => { (g[d.category] ||= []).push(d); });
    return g;
  }, [filtered]);

  const onDone = () => { setShowForm(false); setEditing(null); reload(); };

  const cvMatches = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return [];
    return (cvData?.cvs || []).filter((c) =>
      `${c.employee_name} ${c.summary || ""} ${(c.keywords || []).join(" ")} ${(c.skills || []).map((s) => s.name).join(" ")} ${(c.titles || []).join(" ")}`.toLowerCase().includes(needle));
  }, [cvData, q]);

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title={t("lib_title")} icon={BookOpen} subtitle={t("lib_sub")}>
        {canManage && (
          <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border px-3 py-2 text-sm hover:bg-secondary" data-testid="lib-upload-btn">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Ladda upp & sortera
            <input ref={upRef} type="file" multiple accept=".pdf,.docx,.txt,.md,.csv,image/*" className="hidden" onChange={onUpload} data-testid="lib-upload-input" />
          </label>
        )}
        {canManage && !showForm && !editing && (
          <Button variant="outline" onClick={retag} disabled={retagging} data-testid="lib-retag-btn">
            {retagging ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1 h-4 w-4" />}Generera nyckelord
          </Button>
        )}
        {canManage && !showForm && !editing && (
          <Button onClick={() => setShowForm(true)} data-testid="lib-new-btn"><Plus className="mr-1 h-4 w-4" />{t("lib_new")}</Button>
        )}
      </PageHeader>

      {(showForm || editing) && canManage && (
        <DocForm initial={editing ? { id: editing.id, title: editing.title, category: editing.category, body: editing.body || "", file_path: editing.file_path, file_name: editing.file_name, tags: editing.tags || [] } : null}
          onDone={onDone} onCancel={() => { setShowForm(false); setEditing(null); }} />
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("lib_search")} data-testid="lib-search" className="min-w-[200px] flex-1" />
        {[["", "Alla branscher"], ["lager", "Lager"], ["e-handel", "E-handel"], ["industri", "Industri"]].map(([v, l]) => (
          <button key={v || "all"} onClick={() => setIndustry(v)} data-testid={`lib-ind-${v || "all"}`}
            className={`rounded-full border px-3 py-1.5 text-xs ${industry === v ? "bg-accent text-accent-foreground" : "bg-background hover:bg-secondary"}`}>{l}</button>
        ))}
      </div>

      {filtered.length === 0 && cvMatches.length === 0 ? (
        <EmptyState icon={BookOpen} title={t("lib_empty_title")} text={t("lib_empty_text")} />
      ) : (
        CAT_ORDER.filter((c) => groups[c]?.length).map((c) => (
          <div key={c} className="mb-6" data-testid={`lib-group-${c}`}>
            <div className="mb-2 text-sm font-semibold text-muted-foreground">{catLabel(c)} ({groups[c].length})</div>
            <div className="space-y-3">
              {groups[c].map((d) => <DocCard key={d.id} d={d} canManage={canManage} onEdit={(doc) => { setEditing(doc); setShowForm(false); }} onChanged={reload} />)}
            </div>
          </div>
        ))
      )}

      {cvMatches.length > 0 && (
        <div className="mt-8" data-testid="lib-cv-matches">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold"><FileText className="h-4 w-4 text-accent" />Kompetens (från CV) ({cvMatches.length})</div>
          <div className="space-y-3">
            {cvMatches.map((c) => (
              <Card key={c.employee_id} className="p-4" data-testid={`lib-cv-${c.employee_id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <FileText className="h-4 w-4 text-accent" />
                  <span className="font-medium">{c.employee_name}</span>
                  <Link to="/cv" className="ml-auto inline-flex items-center gap-1 text-sm text-accent underline" data-testid={`lib-cv-open-${c.employee_id}`}><ExternalLink className="h-4 w-4" />Öppna CV</Link>
                </div>
                {c.summary && <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{c.summary}</p>}
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {(c.keywords || []).slice(0, 10).map((k) => <span key={k} className="rounded-full bg-secondary px-2 py-0.5 text-[11px] text-muted-foreground">#{k}</span>)}
                  {(c.skills || []).slice(0, 8).map((s, i) => <span key={i} className="rounded-md border px-2 py-0.5 text-[11px]">{s.name} {s.level}</span>)}
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {feedPosts.length > 0 && (
        <div className="mt-8">
          <div className="mb-2 text-sm font-semibold">{t("lib_from_feed")}</div>
          <div className="space-y-3">
            {feedPosts.map((p) => (
              <Card key={p.id} className="p-4" data-testid={`lib-feed-${p.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <FileText className="h-4 w-4 text-accent" />
                  <span className="font-medium">{p.title || (p.body || "").slice(0, 50)}</span>
                  <Badge variant="outline">{catLabel(p.category)}</Badge>
                  <Link to={`/flode?post=${p.id}`} className="ml-auto inline-flex items-center gap-1 text-sm text-accent underline" data-testid={`lib-feed-open-${p.id}`}>
                    <ExternalLink className="h-4 w-4" />{t("lib_open_file")}
                  </Link>
                </div>
                {p.body && <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{p.body}</p>}
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
